/**
 * Where to sample the weather across a map view.
 *
 * A lattice in degrees, spaced so there is roughly one sample per `cellPx`
 * screen pixels, snapped to a round step — 0.25°, 1°, 5°... — so that panning
 * re-uses cached samples instead of asking for a fresh, slightly shifted grid.
 */

const STEPS = [0.1, 0.25, 0.5, 1, 2, 3, 5, 10, 15, 20, 30];

export interface GridPoint {
  lat: number;
  lon: number;
}

export function weatherGrid(
  west: number, east: number, south: number, north: number,
  degreesPerPixel: number, cellPx = 150, max = 60,
): { step: number; points: GridPoint[] } {
  const want = degreesPerPixel * cellPx;
  const step = STEPS.find(s => s >= want) ?? 30;
  const points: GridPoint[] = [];
  const lat0 = Math.ceil(Math.max(-80, south) / step) * step;
  const lat1 = Math.min(80, north);
  const lon0 = Math.ceil(west / step) * step;
  for (let lat = lat0; lat <= lat1; lat += step) {
    for (let lon = lon0; lon <= east; lon += step) {
      const wrapped = ((lon + 540) % 360) - 180;
      points.push({ lat: Math.round(lat * 1000) / 1000, lon: Math.round(wrapped * 1000) / 1000 });
    }
  }
  // Too many for one request: thin evenly.
  if (points.length > max) {
    const keep = points.length / max;
    return { step, points: points.filter((_, i) => Math.floor(i % keep) === 0).slice(0, max) };
  }
  return { step, points };
}
